import { useDispatch, useSelector } from 'react-redux'
import { getEnrollment, setEnrollment } from '../store/session'
import { useEffect } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material'
import { capitalize } from 'lodash'
import { useTranslation } from 'react-i18next'
import { stringToColor } from 'components/Avatar'

export default function Enrollments({ children }) {
  const { district, enrollments } = useSelector(state => state.session)
  const dispatch = useDispatch()
  const { t } = useTranslation()

  useEffect(() => {
    enrollments || dispatch(getEnrollment())
  }, [Boolean(enrollments), dispatch])

  const connect = id => dispatch(setEnrollment({ id }))

  useEffect(() => {
    if (district || !enrollments?.length) return
    if (enrollments.length === 1 || ['parent', 'student'].includes(enrollments[0]?.type)) {
      connect(enrollments[0].id)
    }
  }, [enrollments, district])

  if (district) return children

  if (!enrollments) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (enrollments.length === 0) {
    const isParentOrStudent = ['parent', 'student'].includes(enrollments[0]?.type)
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, px: 2 }}>
        <Alert severity="warning" sx={{ fontSize: '1rem' }}>
          {isParentOrStudent
            ? t('You have no active students associated with your account. Please contact the school administration for assistance.')
            : t('Your account is inactive. Please contact the school administration for assistance.')}
        </Alert>
      </Box>
    )
  }

  const isAutoConnect = enrollments.length === 1 || ['parent', 'student'].includes(enrollments[0]?.type)
  if (isAutoConnect) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Dialog open fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" fontWeight={700}>{t('Select Account')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('Choose the account you want to connect to')}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <List disablePadding>
          {enrollments.map((item, i) => {
            const label = item.campusName || item.districtName
            const initials = (label || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
            const color = stringToColor(label || '')
            return (
              <Box key={item.id}>
                {i > 0 && <Divider />}
                <ListItemButton onClick={() => connect(item.id)} sx={{ borderRadius: 1, py: 1.5 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: color, width: 36, height: 36, fontSize: 13, fontWeight: 700 }}>
                      {initials}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={label}
                    secondary={capitalize(item.type)}
                    primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Button size="small" variant="outlined" sx={{ ml: 1, flexShrink: 0 }}>
                    {t('Connect')}
                  </Button>
                </ListItemButton>
              </Box>
            )
          })}
        </List>
      </DialogContent>
    </Dialog>
  )
}
